import { useEffect } from 'react';
import { isTauri } from '../utils/tauri';
import { enableModernWindowStyle, cleanupRoundedCorners } from '../lib/macRoundedCorners';
import { useUiModalStore } from '../store/uiModalStore';
import { useUpdateStore } from '../store/updateStore';
import { disconnectSocket } from '../services/channelService';
// NOTE: the AI socket teardown that used to sit next to disconnectSocket()
// here is gone WITH its socket — AI moved off Phoenix channels entirely to
// direct BYO-key provider calls (services/ai/engine.js, 16.08.2026). There
// is no longer any AI transport holding session-derived state to reset.

/**
 * Window-level chrome and lifecycle:
 *  - macOS native rounded corners + shadow for the frameless window.
 *  - `auth:logout` handler that resets the UI/update stores and invokes
 *    `onLogoutReset` for state still owned by App (shrinks as more stores land).
 *
 * @param {{ onLogoutReset?: () => void }} deps
 */
export function useWindowChrome({ onLogoutReset } = {}) {
  // macOS only: native rounded corners + shadow for frameless window (Rust plugin).
  useEffect(() => {
    if (!isTauri()) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { platform } = await import('@tauri-apps/plugin-os');
        if (platform() !== 'macos') return;
        await enableModernWindowStyle({ cornerRadius: 12 });
        if (cancelled) cleanupRoundedCorners();
      } catch (e) {
        console.warn('macOS window corners:', e);
      }
    })();
    return () => {
      cancelled = true;
      cleanupRoundedCorners();
    };
  }, []);

  /**
   * Windows 10 only: draw the top border ourselves.
   *
   * The window is frameless (`decorations: false`), so the OS draws the frame.
   * Windows 11's DWM gives an undecorated window rounded corners AND a 1px
   * border on all four edges. Windows 10's does not draw the TOP edge — the
   * left, right and bottom come from the drop shadow, and the titlebar bleeds
   * into whatever is behind the window. macOS is unaffected (its border comes
   * from the rounded-corners plugin above).
   *
   * Detected by build number rather than by name: `platform()` reports
   * 'windows' for both, and Windows 11 is 10.0.22000 and up — it still reports
   * a major version of 10, so only the build distinguishes them.
   */
  useEffect(() => {
    if (!isTauri()) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const os = await import('@tauri-apps/plugin-os');
        if (os.platform() !== 'windows') return;

        // `version()` is "10.0.19045" (Win10) or "10.0.22631" (Win11).
        const build = Number(String(os.version?.() ?? '').split('.')[2]);
        // If the build is unreadable, do nothing: a spurious border on
        // Windows 11 would sit inside the rounded corner and look broken,
        // which is worse than the missing edge this fixes.
        if (!Number.isFinite(build) || build >= 22000) return;

        if (!cancelled) document.documentElement.classList.add('win-legacy-frame');
      } catch (e) {
        console.warn('Windows frame detection:', e);
      }
    })();

    return () => {
      cancelled = true;
      document.documentElement.classList.remove('win-legacy-frame');
    };
  }, []);

  // Clear all application state on logout.
  useEffect(() => {
    const handleLogout = () => {
      // COMMS-03: some logout paths (apiClient.js's 401 interceptor on a
      // failed refresh) dispatch `auth:logout` directly without going
      // through authStore.logout() — which is otherwise the only place that
      // tears down the sockets. Make this generic listener the single source
      // of truth for socket teardown so it's covered regardless of which
      // code path fired the event, instead of split-brain realtime silently
      // continuing to deliver the previous account's data. Safe to call
      // unconditionally: the disconnect is an idempotent no-op when already
      // torn down (e.g. authStore.logout() already called it). This also
      // fires disconnectSocket()'s universal addSessionEndedListener signal
      // (see channelService.js) for anything that reaches `auth:logout`
      // without going through authStore.logout() itself — every module that
      // subscribes there (linked accounts, sync-status banners, chat/team/
      // workspace channel state, ...) resets ITSELF, so this handler does
      // not need to enumerate them by name.
      disconnectSocket();
      useUiModalStore.getState().reset();
      useUpdateStore.getState().reset();
      onLogoutReset?.();
      document.body.style.overflow = '';
      if (process.env.NODE_ENV === 'development') {
        console.log('Application state cleared after logout');
      }
    };
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, [onLogoutReset]);
}
